import { Bounce, Back, gsap } from 'gsap';
import { Helper } from "../../../../dev/utils/Helper";
import { BitmapTextStyleOptions, TextStyleOptions } from "../../../../dev/setting/text/IText";
import { Translator } from '../../../../dev/setting/Translator';
import { SplashView } from '../SplashController';
import { BitmapCountUp } from "../../../component/BitmapCountUp";
import { GameAssets } from "../../../config/texture/GameAssets";
import { TranslationTexts } from "../../../config/translate/TranslationTexts";
import { FontWeight } from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import { GameData } from "../../../server/data/BaseModel";
import { CarFSBox } from '../../../background/CarFSBox';
import { CarTypes } from '../../../server/data/GameDataModels';
import { RoadRage } from '../../../RoadRage';
import { RoadRageSpriteSymbol } from '../../../reel/symbol/RoadRageSpriteSymbol';
import { SpinePlayer } from '../../../../dev/displayobject/SpinePlayer';
import { OverlayItem } from '../../animations/OverlayAnimation';
import { ContinueButton } from '@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton';
import { SoundConfig } from '../../../config/sound/SoundConfig';
import { SoundManager } from '../../../config/sound/SoundManager';

export interface OutroData {
    fsPlayed: number;
    totalAmount: number;
    level: number;
}

export class SplashViewSummary extends PIXI.Container implements SplashView {

    private summaryTxt: PIXI.Text;
    private _timeLine: gsap.core.Timeline;
    private freeSpinsWonTxt: PIXI.Text;
    private carsUpgradedTxt: PIXI.Text;

    private freeSpinsWon: BitmapCountUp;

    private cartActivated: string[] = [];

    private nextSoundLoop: string;
    private nextIntroSoundLoop: string;

    private carBoxes: CarFSBox[] = [];
    private carSymbols: RoadRageSpriteSymbol[] = [];

    private skipped: boolean = false;
    private carBox: PIXI.Container = new PIXI.Container();

    private _continueButton: ContinueButton;

    private DEFAULT_TXT_STYLE: TextStyleOptions;

    private readonly _styleFS: BitmapTextStyleOptions = {
        fontName: GameAssets.BITMAP_FONT_FS_NUMBERS,
        fontSize: 32,
        tint: 0xffffff,
        letterSpacing: 0
    };

    private readonly styleSumm: TextStyleOptions = {
        fontFamily: "Helvetica",
        fontSize: 72,
        fontWeight: FontWeight.EXTRA_BOLD,
        fill: [
            "#fffb00",
            "#c93",
        ],
        stroke: "#600",
        strokeThickness: 4
    };

    private readonly styleTxtFS: TextStyleOptions = {
        "dropShadow": true,
        "dropShadowAngle": 1.6,
        "dropShadowBlur": 2,
        "dropShadowDistance": 4,
        "fill": [
            "white",
            "#aaa"
        ],
        "fontFamily": "Helvetica",
        "fontSize": 52,
        "fontWeight": "bolder",
        "lineJoin": "round",
        "stroke": "444",
        "strokeThickness": 2
    };

    public get redCar(): RoadRageSpriteSymbol {
        return this.carSymbols[0];
    }

    public get greenCar(): RoadRageSpriteSymbol {
        return this.carSymbols[1];
    }

    public get yellowCar(): RoadRageSpriteSymbol {
        return this.carSymbols[2];
    }

    public get redBox(): CarFSBox {
        return this.carBoxes[0];
    }

    public get yellowBox(): CarFSBox {
        return this.carBoxes[1];
    }

    public get greenBox(): CarFSBox {
        return this.carBoxes[2];
    }

    constructor(textStyle: TextStyleOptions) {
        super();

        this.DEFAULT_TXT_STYLE = textStyle;
        this.init();
        this.visible = false;
    }

    public setData(data: GameData): void {

        this.freeSpinsWon.showValue(data.raw.freespinsLeft, false);
        if (data.fsInfo.fsCars) {
            this.cartActivated = Object.keys(data.fsInfo.fsCars);
            this.cartActivated.forEach(v => {
                const Box = this.fetchBoxBytype(v);
                Box.multValue = data.fsInfo.fsCars[v][0];
                Box.splitValue = data.fsInfo.fsCars[v][1];
            });

            this.nextIntroSoundLoop = LoopSoundVal(this.cartActivated.length-1);
            this.nextSoundLoop = introSoundVal(this.cartActivated.length-1);
        }

        function LoopSoundVal(val: number): string {
            return SoundConfig.FS_MUSIC_SETUP_LOOP[val];
        }

        function introSoundVal(val: number): string {
            return SoundConfig.FS_MUSIC_INTRO[val];
        }
    }

    private init(): void {

        for (let i: number = 0; i < 3; i++) {
            this.carBoxes.push(this.carBox.addChild(new CarFSBox(["C", "B", "A"][i],true)));
            this.carSymbols.push(this.carBox.addChild(new RoadRageSpriteSymbol(i, ["WC1", "WC2", "WC3"][i], "normal", 144)))
        }

        this.carBox.scale.set(0.8);
        this.addChild(this.carBox);
        // this.reelAreaDarkness = this.addChild(Helper.createBasicGraphic(0x000000,0.45,-360,-(108*5)/2,720,108*5));
        // this.reelAreaDarkness.zIndex = -100;

        // public static SETUP_SUMMARY:string = "SUMMARY";
        // public static SETUP_CARS_ACTIVE:string = "CARS";
        // public static SETUP_FREESPINS_COLLECTED:string = "FREESPINS COLLECTED";
        // public static SETUP_FREESPINS:string = "FREESPINS";
        // public static SETUP_FREESPINS_COLLECTED:string = "COLLECTED";
        // public static SETUP_FREESPINS_WON:string = "WON";

        // SUMMARY
        this.summaryTxt = this.createNormalText(Translator.translate(TranslationTexts.SETUP_SUMMARY), this.styleSumm)
        this.summaryTxt.position = new PIXI.Point(0, -240);
        this.freeSpinsWonTxt = this.createNormalText(Translator.translate(TranslationTexts.SETUP_FREESPINS_WON), this.styleTxtFS)
        this.freeSpinsWonTxt.position = new PIXI.Point(0, -130);
        this.freeSpinsWonTxt.scale.set(1, 1);
        this.carsUpgradedTxt = this.createNormalText(Translator.translate(TranslationTexts.SETUP_CARS_UPGRADED), this.styleTxtFS)
        this.carsUpgradedTxt.position = new PIXI.Point(0, -120);
        this.carsUpgradedTxt.scale.set(0.8, 0.8);
        
        // FS-COUNTER
        this.freeSpinsWon = this.addChild(new BitmapCountUp(this._styleFS));
        this.freeSpinsWon.position = new PIXI.Point(0, 0);
        this.freeSpinsWon.anchor = new PIXI.Point(0.5, 0.5);
        this.freeSpinsWon.showValue(0);

        this.createInteractiveModules();

        this.onResize();
    }

    private createInteractiveModules(): void {
        this._continueButton = this.addChild(new ContinueButton(">>>"));

        this._continueButton.enable(false);
        this._continueButton.resizeButtonToLabelWithMargin(25, 50, 25, 50);
        this._continueButton.x -= this._continueButton.width / 2;
        this._continueButton.y = 180;
        this._continueButton.addClickCallback(() => {
            this.skipped = true;
            SoundManager.playSoundEffect(SoundConfig.CONTINUE_SETUP_SUM);
            SoundManager.stopSoundEffect(SoundConfig.SUMMARY_SETUP_SPLASH);
            this._continueButton.enable(false);
            this._continueButton.alpha = 0;
            this.alpha = 1;
            gsap.globalTimeline.timeScale(10000);
        });

        this._continueButton.zIndex = 100;
        this._continueButton.alpha = 0;
    }

    private createNormalText(txt: string, style: any): PIXI.Text {
        const txtt = new PIXI.Text(txt, style);
        txtt.anchor.set(0.5, 0.5);
        return this.addChild(txtt);
    }

    public play(): gsap.core.Timeline {

        SoundManager.fadeSound(this.nextIntroSoundLoop,1,0.55,700)

        this._timeLine = Helper.setTimeline(this._timeLine, new gsap.core.Timeline({ onStart: () => { this.visible = true } }));
        this._timeLine.add(() => {
            this.skipped = false;
            this._continueButton.alpha = 1;
            this._continueButton.enable(true);

            SoundManager.playSoundEffect(SoundConfig.SUMMARY_SETUP_SPLASH,undefined,1);
        });

        this._timeLine.fromTo(this, { alpha: 0 }, { duration: 0.5, alpha: 1 }, 0);

        this.resetVal();
        this.FreeSpinsAnimation();
        this.CarGatheredAnimation();

        this.EndResult();

        return this._timeLine;
    }

    private EndResult(): void {
        
        const startTime = this._timeLine.duration();

        this._timeLine.add(() => {
            this.freeSpinsWonTxt.position.set(0, -160);
            this.freeSpinsWon.position.set(0, -110);
            this.carBox.scale.set(1);
        })

        this._timeLine.to(this.carsUpgradedTxt, { alpha:1, x: 0, y: 0, duration: 0.5 }, startTime);
        this._timeLine.to(this.freeSpinsWonTxt, { alpha:1, duration: 0.5 }, startTime);
        this._timeLine.to(this.carsUpgradedTxt.scale, { x: 0.9, y: 0.9, duration: 0.5 }, startTime);
        this._timeLine.to(this.freeSpinsWonTxt.scale, { x: 0.9, y: 0.9, duration: 0.5 }, startTime);

        this._timeLine.to([this.freeSpinsWon.scale], { x: 1.1, y: 1.1, duration: 0.5, ease: Back.easeOut }, startTime + 0.5);
        this._timeLine.to([this.freeSpinsWon], { alpha: 1, duration: 0.5, ease: Back.easeOut }, startTime + 0.5);

        const startX = this.cartActivated.length === 1 ? 0 : this.cartActivated.length === 2 ? -70 : -130;

        this.cartActivated.forEach((cType, i) => {
            const startBoxTime = this._timeLine.duration();

            const Box: CarFSBox = this.fetchBoxBytype(cType);

            this._timeLine.add(() => {
                Box.scale.set(0);
                Box.y = 110;
                Box.position.x = startX + (i * 120);
            }, startBoxTime - 0.26);
            this._timeLine.add(() => {SoundManager.playSoundEffect(SoundConfig.SETUP_CAR_PLUS_TIRE)}, startBoxTime - 0.25);
            this._timeLine.to(Box, { alpha: 1, duration: 0.5 }, startBoxTime - 0.25);
            this._timeLine.to(Box.scale, { x: 1, y: 1, duration: 0.5 }, startBoxTime - 0.25);
        })

        this._timeLine.to(this,{duration:4});
        
        this._timeLine.add(() => {
            this.skipped = false;
            this._continueButton.enable(false);
            this._continueButton.alpha = 0;
        });

        this._timeLine.add(() => { this.alpha = 1; gsap.globalTimeline.timeScale(1); })
    }

    private currSpine: OverlayItem;
    private CarGatheredAnimation(): void {
        const startTextTime = this._timeLine.duration();
        this._timeLine.to(this.carsUpgradedTxt, { alpha: 1, duration: 0.25 }, startTextTime);
        this._timeLine.to(this.carsUpgradedTxt.scale, { x: 1, y: 1, duration: 0.25, ease: Back.easeIn }, startTextTime);
        this._timeLine.add(() => { SoundManager.playSoundEffect(SoundConfig.SUMMARY_SETUP_CAR_WON)}, startTextTime + 0.5);
        // Pop up Car Text.
        this.cartActivated.forEach((cType, i) => {

            const startCarTime:number = this._timeLine.duration();
            const Box:CarFSBox = this.fetchBoxBytype(cType);
            const Car:RoadRageSpriteSymbol = this.fetchCarByType(cType);

            const multValue:number = Box.multValue;
            const splitvalue:number = Box.splitValue;

            Car.alpha = 1;
            Car.mainSp.alpha = 1;

            const dist: number = 3;
            const revealDurr: number = 0.5;
            const durrSpeed: number = 0.5;
            let spawnDelay: number = startCarTime + durrSpeed + revealDurr + 0.2;

            const animationName: string = "Pop_WC_" + cType.slice(2) + "_144";
            const spineDurr: number = SpinePlayer.fetchDuration("WILD", animationName);

            this._timeLine.fromTo(Box, { alpha: 0 }, { duration: revealDurr, alpha: 1 }, startCarTime)
            this._timeLine.fromTo(Box.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: revealDurr }, startCarTime)


            if (cType === CarTypes.RED) {
                Car.position.x = 180;
                this._timeLine.add(() => { SoundManager.playTakeOffSound() })
                this._timeLine.add(Car.CarSumMove(dist, durrSpeed));
            } else if (cType === CarTypes.GREEN) {
                Car.position.x = -280;
                this._timeLine.add(() => { SoundManager.playTakeOffSound() })
                this._timeLine.add(Car.CarSumMove(-dist, durrSpeed));
            } else {
                Car.position.x = 0;
                this._timeLine.add(Car.CarSumMove(0));
            }

            if (cType !== CarTypes.YELLOW) {
                for (let k = 0; k < Math.abs(dist); k++) {
                    this._timeLine.add(() => {
                        if(!this.skipped)
                            RoadRage.overlayAnim.playPlusOneTextPos(carSpawnPos(cType, k), splitvalue,cType,RoadRage.particleManager, 0.8).add(() => {SoundManager.playSoundEffect(SoundConfig.MULT_LAND)})
                    }, spawnDelay)
                    spawnDelay += 0.07;
                }
            } else {
                for (let k = 0; k < 2; k++) {
                    this._timeLine.add(() => {
                        if(!this.skipped)
                            RoadRage.overlayAnim.playPlusOneTextPos(new PIXI.Point(360, 300 + (k * (144*0.6) * 2)), splitvalue,cType,RoadRage.particleManager,0.6).add(() => {SoundManager.playSoundEffect(SoundConfig.MULT_LAND)})
                    }, startCarTime + revealDurr + 0.2 )
                }
            }

            const spineStart: number = this._timeLine.duration() + 0.25;

            this._timeLine.add(() => {
                SoundManager.playSoundEffect(SoundConfig.CAR_TRANSFORM_TO_WILD)
                    this.currSpine = RoadRage.overlayAnim.playSpine(Car, "WILD", { showOnEnd: true, animation: animationName, onStart: () => Car.mainSp.alpha = 0 }, true, this.carBox);
                    this.currSpine.item.zIndex = -2;
                    this.carBox.sortChildren();
                    const bVal: number = multValue;
                    if (bVal)
                        Car.addSumMult(bVal, undefined, cType === CarTypes.GREEN);

                    this._timeLine.add((this.currSpine as OverlayItem).timeline, spineStart);
                    this._timeLine.to(this.currSpine.item, { alpha: 0, duration: 0.5 }, spineStart + spineDurr * 1.5)
                    this._timeLine.to(Car, { alpha: 0, duration: 0.5 }, spineStart + spineDurr * 1.5)
            },spineStart);

            this._timeLine.to(Box, { alpha: 0, duration: 0.5 }, spineStart + spineDurr * 1.5);
        })

        function carSpawnPos(type: string, indx: number): PIXI.Point {
            if (type === CarTypes.RED) {
                return new PIXI.Point(546 - indx * 144, 400);
            }
            return new PIXI.Point(86 + indx * 144, 400);
        }
    }

    private fetchCarByType(type: string): RoadRageSpriteSymbol {
        switch (type) {
            case CarTypes.RED:
                return this.redCar;
            case CarTypes.GREEN:
                return this.greenCar;
            default:
                return this.yellowCar;
        }
    }

    private fetchBoxBytype(type: string): CarFSBox {
        switch (type) {
            case CarTypes.RED:
                return this.redBox;
            case CarTypes.GREEN:
                return this.greenBox;
            default:
                return this.yellowBox;
        }
    }

    private FreeSpinsAnimation(): void {

        // FREESPINS WON
        this._timeLine.fromTo(this.freeSpinsWonTxt, {alpha:0, x: -160, }, { alpha: 1, duration: 0.25, x: 0 }, 0);

        // Collected - Counter
        this.freeSpinsWon.y = -10
        this._timeLine.to(this.freeSpinsWon, { duration: 0.5, alpha: 1 }, 0.25);
        this._timeLine.fromTo(this.freeSpinsWon.scale, { x: 0, y: 0 }, { duration: 0.25, x: 2, y: 2, ease: Bounce.easeOut }, 0.25);

        this._timeLine.add(() => { SoundManager.playSoundEffect(SoundConfig.SUMMARY_SETUP_FS_WON)},0.5);

        this._timeLine.to(this.freeSpinsWonTxt.scale, { duration: 0.25, x: 1, y: 1, ease: Back.easeOut }, 1.25);
        this._timeLine.to([this.freeSpinsWonTxt.scale, this.freeSpinsWon.scale], { duration: 0.5, x: "-=0.5", y: "-=0.5", ease: Back.easeIn }, 1.75);
        this._timeLine.to([this.freeSpinsWon, this.freeSpinsWonTxt], { duration: 0.75, alpha: 0 }, 1.75);
    }

    private resetVal(): void {
        this.carBox.scale.set(0.8);
        //  FS-COUNTER
        this.freeSpinsWonTxt.y = -120;

        this.freeSpinsWon.position = new PIXI.Point(0, 0);
        this.freeSpinsWon.alpha = 0;
        this.freeSpinsWonTxt.alpha = 0;
        this.carsUpgradedTxt.alpha = 0;
        this.carsUpgradedTxt.position.y = -120;
        this.carsUpgradedTxt.scale.set(0.8);

        //  CARS
        this.carBoxes.forEach(b => { b.alpha = 0; b.position.set(320, 40) });
        this.carSymbols.forEach(b => { b.alpha = 0; b.position.set(320, 40); b.resetSum() });
    }

    public stop(): gsap.core.Timeline {
        this._timeLine = Helper.setTimeline(this._timeLine, new gsap.core.Timeline({ onComplete: () => { this.visible = false } }));
        this._timeLine.to(this, { duration: 1, alpha: 0 });

        SoundManager.playSoundEffect(SoundConfig.SPLASH_INTRO_BTN[this.cartActivated.length-1]);

        SoundManager.fadeSound(this.nextIntroSoundLoop,1,0,1000);
        SoundManager.fadeSound(SoundConfig.SUMMARY_SETUP_SPLASH,1,0,1000);
        Helper.setTimeline().to(this,{duration:1}).add(() => { SoundManager.stopSoundEffect(this.nextIntroSoundLoop) })

        SoundManager.stopTakeOffSound();
        SoundManager.stopSoundEffect(SoundConfig.CAR_TRANSFORM_TO_WILD);

        SoundManager.ChangeToLoop([this.nextSoundLoop]);
        SoundManager.startAmbient();

        // SoundManager.startNewFsMusic(this.nextIntroSoundLoop,this.nextSoundLoop);

        return this._timeLine;
    }

    public onResize(): void {
        
        this.sortChildren();
        if (this._timeLine) this._timeLine.progress(1);
    }
}
